import { useCallback, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState } from 'ag-grid-community';
import { MST4030_R01HeaderDto } from './Dto_MST4030_R01';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { aggridHeaderClass } from 'src/components/common/common';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

/** 그리드 컬럼 너비/순서 등 로컬스토리지 키 (GridContextWrapper·Grid_Select 공통) */
const COLUMN_STATE_KEY = 'MST4030_R01-label-modal-column-state';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	dataList: MST4030_R01HeaderDto[];
	defaultMST4030_R01HeaderRow: MST4030_R01HeaderDto;
}

/** MST4030 설비 목록에서 여러 건을 골라 QZ 라벨 일괄 출력 */
export const LabelBulkPrintModal_MST4030_R01 = ({ isOpen, onClose, dataList, defaultMST4030_R01HeaderRow }: Props) => {
	const gridRef = useRef<AgGridReact<MST4030_R01HeaderDto> | null>(null);
	const { printBulkEquipmentLabel, printBulkToolingLabel } = useQzPrint();

	/** 호버 행 — onlyTouchGridStyle과 연동 */
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	/** 컬럼 상태 저장용 setter만 사용 (값은 Grid 쪽에서 복원) */
	const [, setColumnState] = useState<ColumnState[] | null>(null);

	/** 선택 키: equipmentCd 배열 (AG Grid 기본 selection 대신 수동 관리) */
	const [selectedCds, setSelectedCds] = useState<string[]>([]);
	const [isPrinting, setIsPrinting] = useState(false);

	const isAllSelected = dataList.length > 0 && selectedCds.length === dataList.length;

	const {
		getRowId,
	} = useGridSettingContent({
		gridRef,
		key: 'equipmentCd',
		gridData: dataList,
		defaultGridData: defaultMST4030_R01HeaderRow,
		selectedRow: selectedCds,
	});

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'equipmentCd' as const,
		selectedIds: selectedCds as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedCds(ids.map(String)),
	}), [selectedCds]);

	/** 전체 선택 / 전체 해제 토글 */
	const handleSelectAll = useCallback(() => {
		if (isAllSelected) {
			setSelectedCds([]);
		} else {
			setSelectedCds(dataList.map((row) => row.equipmentCd));
		}
	}, [isAllSelected, dataList]);

	/** 선택된 설비만 골라 QZ 일괄 출력 API에 넘길 최소 필드로 매핑 */
	const handlePrint = useCallback(async () => {
		if (selectedCds.length === 0 || isPrinting) return;
		setIsPrinting(true);
		try {
			const selectedRows = dataList.filter((row) => selectedCds.includes(row.equipmentCd));
			const labelDataList = selectedRows
				.map((row) => ({
					equipmentCd: row.equipmentCd,
					equipmentNm: row.equipmentNm ?? '',
					equipmentSpec: row.equipmentSpec ?? '',
				}));

			const isTooling = selectedRows.some((row) => row.equipmentType === 'METALMASK');

			if (isTooling) {
				await printBulkToolingLabel(labelDataList);
			} else {
				await printBulkEquipmentLabel(labelDataList);
			}
		} finally {
			setIsPrinting(false);
		}
	}, [selectedCds, dataList, isPrinting, printBulkEquipmentLabel, printBulkToolingLabel]);

	/** 모달 닫을 때 선택·Shift 앵커 초기화 후 부모 onClose */
	const handleClose = useCallback(() => {
		setSelectedCds([]);
		onClose();
	}, [onClose]);

	/** selectedCds와 rowClassRules로 행 하이라이트 (Grid 기본 selection 미사용) */
	const rowClassRules = useMemo(() => ({
		selected: (params: { data?: MST4030_R01HeaderDto }) => {
			if (!params.data?.equipmentCd) return false;
			return selectedCds.includes(params.data.equipmentCd);
		},
	}), [selectedCds]);

	/** 호버·선택 상태에 맞춘 셀 스타일 (그리드 공통 유틸) */
	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'equipmentCd')(params), []);

	/** 행번호 + 설비코드/명/유형/상태 (라벨 출력 전 목록 확인용) */
	const baseColumnDefs: ColDef<MST4030_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '설비코드', colId: 'equipmentCd', field: 'equipmentCd', width: 110,
			cellStyle: (p) => getCellStyle(p, 'equipmentCd'),
		},
		{
			headerName: '설비명',
			colId: 'equipmentNm',
			field: 'equipmentNm',
			flex: 1,
			cellStyle: (p) => getCellStyle(p, 'equipmentNm'),
		},
		{
			headerName: '설비유형',
			colId: 'equipmentTypeNm',
			field: 'equipmentTypeNm',
			width: 120,
			cellStyle: (p) => getCellStyle(p, 'equipmentTypeNm'),
		},
		{
			headerName: '상태', colId: 'eqmStatusNm', field: 'eqmStatusNm', width: 80,
			cellStyle: (p) => getCellStyle(p, 'eqmStatusNm'),
		},
	], [getCellStyle]);

	/** 하단 고정 행: 총 건수 표시 (equipmentCd 칸에 문자열) — 클릭은 handleRowClick에서 제외 */
	const pinnedBottomRowData = useMemo(() => ([{
		rowIndex: '',
		equipmentCd: `${dataList.length.toLocaleString()}건`,
		editable: false,
	} as unknown as MST4030_R01HeaderDto]), [dataList.length]);

	if (!isOpen) return null;

	// 배경(딤)만 클릭 시 닫기 — 모달 패널 클릭은 target !== currentTarget 이라 무시
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
			onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
		>
			<div
				className="bg-white rounded-[6px] shadow-xl flex flex-col"
				style={{ width: '750px', height: '620px' }}
			>
				<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
					<span className="font-semibold text-[14px]">라벨 출력</span>
					{/* <button
						type="button"
						onClick={handleClose}
						className="text-gray-400 hover:text-gray-700 text-[18px] leading-none"
						aria-label="??"
					>
						X
					</button> */}
				</div>

				{/* 툴바: 전체 선택 토글 · 선택 해제(1건 이상) · 건수 · QZ 출력 */}
				<div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50">
					<button
						type="button"
						onClick={handleSelectAll}
						className="px-3 py-1.5 text-[12px] font-medium text-white bg-blue-dark hover:bg-blue-600 rounded-[4px]"
					>
						{isAllSelected ? '전체 해제' : '전체 선택'}
					</button>

					{selectedCds.length > 0 && (
						<button
							type="button"
							onClick={() => {
								setSelectedCds([]);
							}}
							className="px-3 py-1.5 text-[12px] font-medium text-white bg-blue-dark hover:bg-blue-600 rounded-[4px]"
						>
							선택 해제
						</button>
					)}

					<span className="text-[12px] text-gray-500">
						{selectedCds.length > 0 ? `${selectedCds.length}건 선택` : ''}
					</span>
					<div className="flex-1" />
					<button
						type="button"
						onClick={handlePrint}
						disabled={selectedCds.length === 0 || isPrinting}
						className={`px-4 py-1.5 text-[12px] font-semibold text-white rounded-[4px]
							${selectedCds.length === 0 || isPrinting
								? 'bg-gray-300 cursor-not-allowed'
								: 'bg-blue-dark hover:bg-blue-600'}`}
					>
						{isPrinting ? '출력 중...' : '출력'}
					</button>
				</div>
				<div className="flex-1 px-2 py-2 min-h-0">
					<GridContextWrapper<MST4030_R01HeaderDto>
						gridRef={gridRef}
						paddedGridData={dataList}
						initSelected={() => {
							setSelectedCds([]);
						}}
						defaultRowData={defaultMST4030_R01HeaderRow}
						baseColumnDefs={baseColumnDefs}
						setColumnState={setColumnState}
						localStorageKey={COLUMN_STATE_KEY}
						enableDataHistory={false}
					>
						<Grid_Select<MST4030_R01HeaderDto>
							ref={gridRef}
							rowData={dataList}
							columnDefs={baseColumnDefs}
							defaultColDef={{ ...defaultColDef, headerClass: aggridHeaderClass }}
							getRowId={getRowId}
							shiftRangeSelection={shiftRangeSelectionConfig}
							rowClassRules={rowClassRules}
							pinnedBottomRowData={pinnedBottomRowData}
							suppressRowClickSelection
							setHoveredRowIndex={setHoveredRowIndex}
							columnStateKey={COLUMN_STATE_KEY}
							setColumnState={(state) => setColumnState(state)}
						/>
					</GridContextWrapper>
				</div>
			</div>
		</div>
	);
};
